import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import PropTypes from "prop-types";
import Breadcrumbs from "../../../../../components/Common/Breadcrumb";
import {
	Button,
	Col,
	Form,
	FormFeedback,
	FormGroup,
	Input,
	Label,
	Row,
} from "reactstrap";
import { useFormik } from "formik";
import withRouter from "../../../../../components/Common/withRouter";
import { useNavigate } from "react-router-dom";
import userService from "services/UserService";
import ReactInputMask from "react-input-mask";
import Swal from "sweetalert2";
import { registerLocale } from "react-datepicker";
import pt from "date-fns/locale/pt-BR";
import userRoleService from "services/UserRoleService";
import organizationalStructureService from "services/OrganizationalStructureService";
import companyService from "services/CompanyService";
import authPermissionService from "services/AuthPermissionService";
import Loading from "components/Common/Loading";
import { CheckSquare, FloppyDisk, Square } from "@phosphor-icons/react";
import { Checkbox } from "@mui/material";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import InactiveUserConflictModal from "./InactiveUserConflictModal";
registerLocale("pt-BR", pt);

const FormUsuario = (props) => {
	const [data, setData] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [funcoes, setFuncoes] = useState([]);
	const [organizacoes, setOrganizacoes] = useState([]);
	const [empresaAtual, setEmpresaAtual] = useState(null);
	const [permissions, setPermissions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isAdmin, setIsAdmin] = useState(false);
	const [isFormReady, setIsFormReady] = useState(false);
	const [inactiveConflictModal, setInactiveConflictModal] = useState({
		open: false,
		inactiveUsers: [],
	});

	document.title = "Senda | Usuário";

	const params = props.router.params;
	const id = params?.id;

	const navigate = useNavigate();

	const genders = [
		{ value: "1", label: "Masculino" },
		{ value: "2", label: "Feminino" },
	];

	const validation = useFormik({
		// enableReinitialize : use this flag when initial values needs to be changed
		enableReinitialize: true,

		initialValues: {
			id: data?.id || null,
			name: data?.name || null,
			lastname: data?.lastname || null,
			email: data?.email || null,
			phone: data?.phone || null,
			birthdate: data?.birthdate || null,
			admission_date: data?.admission_date || null,
			gender: data?.gender || null,
			role_id: data?.role_id || null,
			organization_id: data?.organization_id || null,
			scholarity: data?.scholarity || null,
			legal_contact: data?.legal_contact || null,
			company_contact: data?.company_contact || null,
			is_organization_leader: data?.is_organization_leader || null,
			permission_id: data?.permission_id || null,
			password: "",
			confirm_password: "",
			is_admin: data?.is_admin || false,
		},

		validate: (values) => {
			const errors = {};

			if (!values.name) {
				errors.name = "Campo obrigatório";
			}
			if (!values.lastname) {
				errors.lastname = "Campo obrigatório";
			}
			if (!values.email) {
				errors.email = "Campo obrigatório";
			} else if (values.email.includes(" ")) {
				errors.email = "O e-mail não pode conter espaços";
			} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
				errors.email = "E-mail inválido";
			}
			if (!values.phone) {
				errors.phone = "Campo obrigatório";
			}
			if (!values.password && !values.id) {
				errors.password = "Campo obrigatório";
			}
			if (!values.confirm_password && !values.id) {
				errors.confirm_password = "Campo obrigatório";
			}
			if (values.password !== values.confirm_password) {
				errors.confirm_password = "As senhas não coincidem";
			}
			if (!values.permission_id && !values.id) {
				errors.permission_id = "Campo obrigatório";
			}

			return errors;
		},

		onSubmit: async (values, { setSubmitting }) => {
			const errors = await validation.validateForm(values);
			if (Object.keys(errors).length === 0) {
				setLoading(true);
				try {
					const formValues = {
						...values,
						email: (values.email || "").trim(),
					};
					if (id) {
						await userService.put(id, formValues);
						SendaCustomSwal.fire({
							iconHtml: '<i class="success-icon"></i>',
							title: "Sucesso",
							text: "Usuário editado com sucesso!",
							customClass: { confirmButton: "custom-confirm-button" },
						}).then((result) => {
							if (result.isConfirmed) {
								navigate("../lista-usuarios");
								window.location.reload();
							}
						});
					} else {
						await userService.post(formValues);
						SendaCustomSwal.fire({
							iconHtml: '<i class="success-icon"></i>',
							title: "Sucesso",
							text: "Usuário criado com sucesso!",
							customClass: { confirmButton: "custom-confirm-button" },
						}).then((result) => {
							if (result.isConfirmed) {
								navigate("../lista-usuarios");
								window.location.reload();
							}
						});
					}
				} catch (error) {
					const status = error?.response?.status;
					const data = error?.response?.data;
					if (status === 400 && data?.message) {
						SendaCustomSwal.fire({
							iconHtml: '<i class="error-icon"></i>',
							title: "E-mail já cadastrado",
							text: data.message,
							customClass: { confirmButton: "custom-confirm-button" },
						});
					} else if (
						status === 409 &&
						data?.code === "INACTIVE_USER_EXISTS" &&
						(data?.inactiveUsers?.length > 0 || data?.userId)
					) {
						const list = data.inactiveUsers?.length
							? data.inactiveUsers
							: [{ id: data.userId }];
						setInactiveConflictModal({ open: true, inactiveUsers: list });
					} else {
						SendaCustomSwal.fire({
							iconHtml: '<i class="error-icon"></i>',
							title: "Oops...",
							text: data?.message || "Algo deu errado!",
							customClass: { confirmButton: "custom-confirm-button" },
						});
					}
				} finally {
					setLoading(false);
				}
			}
			setSubmitting(false);
		},
	});

	const getItems = async () => {
		try {
			const response = await userService.getById(id);

			setData(response);
		} catch (error) {
			console.log(error);
		}
	};

	async function getPermissions() {
		try {
			const response = await authPermissionService.get();
			const permissions = response.map((item) => {
				return {
					value: item.id,
					label: item.name,
				};
			});
			setPermissions(permissions);
		} catch (error) {
			console.log(error);
		}
	}

	async function getFuncoes() {
		try {
			const response = await userRoleService.get();
			const funcoes = response.map((item) => {
				return {
					value: item.id,
					label: item.name,
				};
			});
			setFuncoes(funcoes);
			setIsLoading(false);
		} catch (error) {
			console.log(error);
		}
	}

	const buscaEmpresaAtual = async () => {
		const company_id = localStorage.getItem("company_id");
		const company = await companyService.getById(company_id);
		const empresa = {
			value: Number(company_id),
			label: company.name,
		};
		setEmpresaAtual(empresa);
	};

	async function getOrganizacoes() {
		try {
			const response = await organizationalStructureService.get();

			const options = response.map((item) => ({
				value: item.id,
				label: item.name,
			}));
			setOrganizacoes(options);
			setIsLoading(false);
		} catch (error) {
			console.log(error);
		}
	}

	const getUserInformation = async () => {
		try {
			const response = await userService.getUserInfo();

			setIsAdmin(response.is_admin);
		} catch (e) {
			console.log(e);
		}
	};

	const getAll = async () => {
		await getUserInformation();
		if (id) {
			await getItems();
		}
		await getPermissions();
		await getFuncoes();
		await getOrganizacoes();
		await buscaEmpresaAtual();
	};

	useEffect(() => {
		getAll();
	}, []);

	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
	};

	useEffect(() => {
		if (id && data) {
			setIsFormReady(true);
		}

		if (!id) {
			setIsFormReady(true);
		}
	}, [data, id]);

	return (
		<Loading isLoading={isLoading}>
			<div className="page-content">
				<Breadcrumbs
					title="Usuários"
					breadcrumbItem="Cadastro de usuário"
					backLink="/lista-usuarios"
				/>

				<div
					className="container-fluid"
					style={{
						backgroundColor: "white",
						padding: 20,
						borderRadius: 10,
						marginTop: 25,
					}}
				>
					<Form
						className="needs-validation"
						onSubmit={(e) => {
							e.preventDefault();
							validation.handleSubmit();
							return false;
						}}
					>
						<Row>
							<Col md="6">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Nome Completo{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										name="name"
										placeholder="Nome Completo"
										type="text"
										className="form-control"
										id="validationCustom01"
										onChange={validation.handleChange}
										onBlur={validation.handleBlur}
										value={validation.values.name || ""}
										invalid={
											validation.touched.name &&
											validation.errors.name
												? true
												: false
										}
									/>
									{validation.touched.name &&
									validation.errors.name ? (
										<FormFeedback type="invalid">
											{validation.errors.name}
										</FormFeedback>
									) : null}
								</FormGroup>
							</Col>

							<Col md="6">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Apelido{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										name="lastname"
										placeholder="Apelido"
										type="text"
										className="form-control"
										id="validationCustom01"
										onChange={validation.handleChange}
										onBlur={validation.handleBlur}
										value={validation.values.lastname || ""}
										invalid={
											validation.touched.lastname &&
											validation.errors.lastname
												? true
												: false
										}
									/>
									{validation.touched.lastname &&
									validation.errors.lastname ? (
										<FormFeedback type="invalid">
											{validation.errors.lastname}
										</FormFeedback>
									) : null}
								</FormGroup>
							</Col>
						</Row>

						<Row>
							<Col md="6">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Email{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										name="email"
										placeholder="Email"
										type="text"
										className="form-control"
										id="validationCustom01"
										onChange={validation.handleChange}
										onBlur={validation.handleBlur}
										value={validation.values.email || ""}
										invalid={
											validation.touched.email &&
											validation.errors.email
												? true
												: false
										}
									/>
									{validation.touched.email &&
									validation.errors.email ? (
										<FormFeedback type="invalid">
											{validation.errors.email}
										</FormFeedback>
									) : null}
								</FormGroup>
							</Col>

							<Col md="6">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Telefone{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<ReactInputMask
										name="phone"
										placeholder="Telefone"
										type="text"
										className={`form-control ${
											validation.touched.phone &&
											validation.errors.phone
												? "is-invalid"
												: ""
										}`}
										mask="(99) 99999-9999"
										onChange={validation.handleChange}
										onBlur={validation.handleBlur}
										value={validation.values.phone || ""}
										invalid={
											validation.touched.phone &&
											validation.errors.phone
												? true
												: false
										}
									/>
									{validation.touched.phone &&
									validation.errors.phone ? (
										<FormFeedback type="invalid">
											{validation.errors.phone}
										</FormFeedback>
									) : null}
								</FormGroup>
							</Col>
						</Row>

						<Row>
							<Col md="6">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Data de nascimento
									</Label>
									<Input
										name="birthdate"
										type="date"
										className="form-control"
										placeholderText="Data de nascimento"
										onChange={validation.handleChange}
										onBlur={validation.handleBlur}
										value={
											validation.values.birthdate || ""
										}
										invalid={
											validation.touched.birthdate &&
											validation.errors.birthdate
										}
									/>
								</FormGroup>
							</Col>

							<Col md="6">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Gênero
									</Label>
									<Select
										styles={customStyles}
										placeholder="Selecione o Gênero"
										onChange={(selectedOption) => {
											const selectedValue = selectedOption
												? selectedOption.value
												: "";
											validation.setFieldValue(
												"gender",
												selectedValue,
											);
										}}
										value={genders.find(
											(option) =>
												option.value ===
												validation.values.gender,
										)}
										options={genders}
									/>
								</FormGroup>
							</Col>
						</Row>

						<Row>
							<Col md="4">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Departamento
									</Label>
									<Select
										styles={customStyles}
										placeholder="Selecione o departamento"
										onChange={(selectedOption) => {
											const selectedValue = selectedOption
												? selectedOption.value
												: null;
											validation.setFieldValue(
												"organization_id",
												selectedValue,
											);
										}}
										value={organizacoes?.find(
											(option) =>
												option.value ===
												validation.values
													.organization_id,
										)}
										options={organizacoes}
									/>
								</FormGroup>
							</Col>

							<Col md="4">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Função
									</Label>
									<Select
										styles={customStyles}
										placeholder="Selecione a função"
										onChange={(selectedOption) => {
											const selectedValue = selectedOption
												? selectedOption.value
												: "";
											validation.setFieldValue(
												"role_id",
												selectedValue,
											);
										}}
										options={funcoes}
										value={funcoes?.find(
											(option) =>
												option.value ===
												validation.values.role_id,
										)}
									/>
								</FormGroup>
							</Col>
							<Col md="4">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Data de Admissão
									</Label>
									<Input
										name="admission_date"
										type="date"
										className="form-control"
										placeholderText="Data de admissão"
										onChange={validation.handleChange}
										onBlur={validation.handleBlur}
										value={
											validation.values.admission_date ||
											""
										}
										invalid={
											validation.touched.admission_date &&
											validation.errors.admission_date
										}
									/>
								</FormGroup>
							</Col>
						</Row>

						{!id && (
							<Row>
								<Col md="6">
									{/* <FormGroup className="mb-3"> */}
									<Label>Empresa</Label>
									<Select
										styles={customStyles}
										placeholder="Selecione a empresa"
										isDisabled={true}
										value={empresaAtual}
									/>
									{/* </FormGroup> */}
								</Col>
								<Col md="6">
									<FormGroup className="mb-3">
										<Label>
											Permissão{" "}
											<span style={{ color: "red" }}>
												*
											</span>
										</Label>
										<Select
											value={permissions.find(
												(option) =>
													option.value ===
													validation.values
														.permission_id,
											)}
											onChange={(option) =>
												validation.setFieldValue(
													"permission_id",
													option.value,
												)
											}
											options={permissions}
											styles={customStyles}
											isClearable={true}
											placeholder="Selecione a permissão"
										/>
										{validation.touched.permission_id &&
										validation.errors.permission_id ? (
											<FormFeedback
												type="invalid"
												style={{ display: "block" }}
											>
												{
													validation.errors
														.permission_id
												}
											</FormFeedback>
										) : null}
									</FormGroup>
								</Col>
							</Row>
						)}
						<Row>
							<Col md="6">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Senha{" "}
										{!id && (
											<span style={{ color: "red" }}>
												*
											</span>
										)}
									</Label>
									<Input
										name="password"
										placeholder="Senha"
										type="password"
										autoComplete="new-password"
										className="form-control"
										id="validationCustom01"
										onChange={validation.handleChange}
										onBlur={validation.handleBlur}
										value={validation.values.password || ""}
										invalid={
											validation.touched.password &&
											validation.errors.password
												? true
												: false
										}
									/>
									{validation.touched.password &&
									validation.errors.password ? (
										<FormFeedback type="invalid">
											{validation.errors.password}
										</FormFeedback>
									) : null}
								</FormGroup>
							</Col>
							<Col md="6">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Confirmação de senha{" "}
										{!id && (
											<span style={{ color: "red" }}>
												*
											</span>
										)}
									</Label>
									<Input
										name="confirm_password"
										placeholder="Confirmação de senha"
										type="password"
										className="form-control"
										id="validationCustom01"
										onChange={validation.handleChange}
										onBlur={validation.handleBlur}
										value={
											validation.values
												.confirm_password || ""
										}
										invalid={
											validation.touched
												.confirm_password &&
											validation.errors.confirm_password
												? true
												: false
										}
									/>
									{validation.touched.confirm_password &&
									validation.errors.confirm_password ? (
										<FormFeedback type="invalid">
											{validation.errors.confirm_password}
										</FormFeedback>
									) : null}
								</FormGroup>
							</Col>
						</Row>
						<Row>
							<Col md="3">
								<FormGroup className="mb-3">
									<label
										className="form-check-label"
										htmlFor="is_organization_leader"
									>
										{isFormReady && (
											<Checkbox
												id="is_organization_leader"
												name="is_organization_leader"
												defaultChecked={
													validation.values
														.is_organization_leader
												}
												onChange={(event) => {
													const value = event.target
														.checked
														? 1
														: null;
													validation.setFieldValue(
														"is_organization_leader",
														value,
													);
												}}
												style={{
													background: "none",
													border: "none",
													padding: 0,
													cursor: "pointer",
													color: "black",
													marginRight: 10,
												}}
												icon={<Square size={16} />}
												checkedIcon={
													<CheckSquare size={16} />
												}
											/>
										)}
										Líder
									</label>
								</FormGroup>
							</Col>
							<Col md="3">
								<FormGroup className="mb-3">
									{isFormReady && (
										<Checkbox
											id="company_contact"
											name="company_contact"
											defaultChecked={
												validation.values
													.company_contact
											}
											onChange={(event) => {
												const value = event.target
													.checked
													? 1
													: null;
												validation.setFieldValue(
													"company_contact",
													value,
												);
											}}
											style={{
												background: "none",
												border: "none",
												padding: 0,
												cursor: "pointer",
												color: "black",
												marginRight: 10,
											}}
											icon={<Square size={16} />}
											checkedIcon={
												<CheckSquare size={16} />
											}
										/>
									)}
									<label
										className="form-check-label"
										htmlFor="company_contact"
										style={{ marginLeft: 10 }}
									>
										Contato da empresa
									</label>
								</FormGroup>
							</Col>
							<Col md="3">
								<FormGroup className="mb-3">
									{isFormReady && (
										<Checkbox
											id="legal_contact"
											name="legal_contact"
											defaultChecked={
												validation.values.legal_contact
											}
											onChange={(event) => {
												const value = event.target
													.checked
													? 1
													: null;
												validation.setFieldValue(
													"legal_contact",
													1,
												);
											}}
											style={{
												background: "none",
												border: "none",
												padding: 0,
												cursor: "pointer",
												color: "black",
												marginRight: 10,
											}}
											icon={<Square size={16} />}
											checkedIcon={
												<CheckSquare size={16} />
											}
										/>
									)}
									<label
										className="form-check-label"
										htmlFor="legal_contact"
										style={{ marginLeft: 10 }}
									>
										Contato Legal
									</label>
								</FormGroup>
							</Col>
							{isAdmin && (
								<Col md="3">
									<FormGroup className="mb-3">
										<label
											className="form-check-label"
											htmlFor="is_admin"
										>
											{isFormReady && (
												<Checkbox
													id="is_admin"
													name="is_admin"
													defaultChecked={
														!!data?.is_admin
													}
													onChange={(event) => {
														console.log(
															"Checkbox value:",
															event.target
																.checked,
														);
														validation.setFieldValue(
															"is_admin",
															event.target
																.checked,
														);
													}}
													style={{
														background: "none",
														border: "none",
														padding: 0,
														cursor: "pointer",
														color: "black",
														marginRight: 10,
													}}
													icon={<Square size={16} />}
													checkedIcon={
														<CheckSquare
															size={16}
														/>
													}
												/>
											)}
											Admin
										</label>
									</FormGroup>
								</Col>
							)}
						</Row>
						<div
							className="page-title-right"
							style={{ display: "flex", justifyContent: "end" }}
						>
							<Button
								style={{
									background: "none",
									padding: 10,
									cursor: "pointer",
									color: "black",
									marginLeft: 10,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 2,
									width: 150,
									marginTop: 5,
								}}
								type="submit"
							>
								{loading ? (
									<span
										className="spinner-border spinner-border-sm mr-1"
										style={{ marginRight: 10 }}
									></span>
								) : null}
								<FloppyDisk size={16} /> Salvar
							</Button>
						</div>
					</Form>
				</div>
			</div>
			<InactiveUserConflictModal
				isOpen={inactiveConflictModal.open}
				toggle={() =>
					setInactiveConflictModal((s) => ({ ...s, open: !s.open }))
				}
				inactiveUsers={inactiveConflictModal.inactiveUsers}
				formValues={validation.values}
				onReactivated={() => {
					navigate("../lista-usuarios");
					window.location.reload();
				}}
				onReplaced={() => {
					navigate("../lista-usuarios");
					window.location.reload();
				}}
			/>
		</Loading>
	);
};
FormUsuario.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default withRouter(FormUsuario);
